import { useCallback, useEffect, useMemo, useState } from 'react'

import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api.js'

import {
    FOCUS_HIGHLIGHT_FALLBACK_SEARCH_RADIUS,
    FOCUS_HIGHLIGHT_PAGE_SEARCH_RADIUS,
    buildSearchVariants,
    collapseHighlightsToDominantBlock,
    countContainedAnchors,
    extractFocusBlockLines,
    extractKeywordAnchors,
    extractLineAnchors,
    extractNumericAnchors,
    getBoundingRectangle,
    normalizeFocusHighlightTerm,
    shouldPreferBlockFocusHighlight,
    type NormalizedHighlight,
} from './highlight-engine'

type FocusHighlightValue = { term: string; page?: number | null; align?: 'exact' | 'offset' } | null

type UseFocusHighlightsParams = {
    collectHighlightsForPage: (pageNumber: number, variants: string[]) => Promise<NormalizedHighlight[]>
    collectNormalizedPageText: (pageNumber: number) => Promise<string>
    focusHighlight: FocusHighlightValue
    onFocusHighlightChange: (value: FocusHighlightValue) => void
    pdfDocument: PDFDocumentProxy | null
}

type UseFocusHighlightsResult = {
    focusHighlights: Record<number, NormalizedHighlight[]>
    focusPage: number | null
    resetFocusHighlights: () => void
    trimmedFocusTerm: string
}

export const useFocusHighlights = ({
    collectHighlightsForPage,
    collectNormalizedPageText,
    focusHighlight,
    onFocusHighlightChange,
    pdfDocument,
}: UseFocusHighlightsParams): UseFocusHighlightsResult => {
    const [focusHighlights, setFocusHighlights] = useState<Record<number, NormalizedHighlight[]>>({})
    const trimmedFocusTerm = useMemo(() => focusHighlight?.term.trim() ?? '', [focusHighlight])
    const normalizedFocusTerm = useMemo(() => normalizeFocusHighlightTerm(trimmedFocusTerm), [trimmedFocusTerm])
    const focusTermRaw = focusHighlight?.term ?? ''
    const focusPage = focusHighlight?.page ?? null
    const focusAlign = focusHighlight?.align

    const resetFocusHighlights = useCallback(() => {
        setFocusHighlights({})
    }, [])

    useEffect(() => {
        let isCancelled = false

        const computeFocus = async () => {
            if (!pdfDocument || !normalizedFocusTerm) {
                resetFocusHighlights()
                return
            }

            const variants = buildSearchVariants(normalizedFocusTerm)
            const preferBlockFocusHighlight = shouldPreferBlockFocusHighlight(normalizedFocusTerm)
            const focusBlockLines = extractFocusBlockLines(normalizedFocusTerm)
            const totalPages = pdfDocument.numPages
            const targetPages: number[] = []
            const visitedPages = new Set<number>()

            const clampedFocusPage = focusPage && focusPage >= 1 && focusPage <= totalPages ? Math.round(focusPage) : null

            if (clampedFocusPage) {
                const basePage = clampedFocusPage
                const orderedCandidates = [basePage]
                for (let offset = 1; offset <= FOCUS_HIGHLIGHT_PAGE_SEARCH_RADIUS; offset += 1) {
                    const upperCandidate = basePage + offset
                    if (upperCandidate >= 1 && upperCandidate <= totalPages) {
                        orderedCandidates.push(upperCandidate)
                    }

                    const lowerCandidate = basePage - offset
                    if (lowerCandidate >= 1 && lowerCandidate <= totalPages) {
                        orderedCandidates.push(lowerCandidate)
                    }
                }
                orderedCandidates.forEach((pageNumber) => visitedPages.add(pageNumber))
                targetPages.push(...orderedCandidates)
            } else {
                const allPages = Array.from({ length: totalPages }, (_, idx) => idx + 1)
                allPages.forEach((pageNumber) => visitedPages.add(pageNumber))
                targetPages.push(...allPages)
            }
            const nextHighlights: Record<number, NormalizedHighlight[]> = {}
            const pageHighlightRawCounts = new Map<number, number>()
            const getPageHighlightCount = (pageNumber: number): number => pageHighlightRawCounts.get(pageNumber) ?? 0

            for (const pageNumber of targetPages) {
                if (isCancelled) {
                    return
                }

                try {
                    const rawPageRects = await collectHighlightsForPage(pageNumber, variants)
                    if (rawPageRects.length > 0) {
                        const pageRects = preferBlockFocusHighlight ? collapseHighlightsToDominantBlock(rawPageRects) : rawPageRects
                        nextHighlights[pageNumber] = pageRects
                        pageHighlightRawCounts.set(pageNumber, rawPageRects.length)
                    }
                } catch {
                    // Continue without focus highlights for this page if detection fails.
                }
            }

            if (clampedFocusPage) {
                const hasPrimaryRadiusHighlight = targetPages.some((pageNumber) => (nextHighlights[pageNumber]?.length ?? 0) > 0)
                if (!hasPrimaryRadiusHighlight && FOCUS_HIGHLIGHT_FALLBACK_SEARCH_RADIUS > FOCUS_HIGHLIGHT_PAGE_SEARCH_RADIUS) {
                    const expandedPages: number[] = []
                    for (
                        let offset = FOCUS_HIGHLIGHT_PAGE_SEARCH_RADIUS + 1;
                        offset <= FOCUS_HIGHLIGHT_FALLBACK_SEARCH_RADIUS;
                        offset += 1
                    ) {
                        const upperCandidate = clampedFocusPage + offset
                        if (upperCandidate <= totalPages && !visitedPages.has(upperCandidate)) {
                            visitedPages.add(upperCandidate)
                            expandedPages.push(upperCandidate)
                        }

                        const lowerCandidate = clampedFocusPage - offset
                        if (lowerCandidate >= 1 && !visitedPages.has(lowerCandidate)) {
                            visitedPages.add(lowerCandidate)
                            expandedPages.push(lowerCandidate)
                        }
                    }

                    for (const pageNumber of expandedPages) {
                        if (isCancelled) {
                            return
                        }

                        targetPages.push(pageNumber)
                        try {
                            const rawPageRects = await collectHighlightsForPage(pageNumber, variants)
                            if (rawPageRects.length > 0) {
                                const pageRects = preferBlockFocusHighlight ? collapseHighlightsToDominantBlock(rawPageRects) : rawPageRects
                                nextHighlights[pageNumber] = pageRects
                                pageHighlightRawCounts.set(pageNumber, rawPageRects.length)
                            }
                        } catch {
                            // Continue searching other fallback pages if this page fails.
                        }
                    }
                }
            }

            const lineAnchors = clampedFocusPage ? extractLineAnchors(normalizedFocusTerm) : []
            const keywordAnchors = clampedFocusPage ? extractKeywordAnchors(normalizedFocusTerm) : []
            const numericAnchors = clampedFocusPage ? extractNumericAnchors(normalizedFocusTerm) : []
            const lineVariantGroups =
                clampedFocusPage && focusBlockLines.length > 1 ? focusBlockLines.map((line) => buildSearchVariants(line)) : []
            const pageLineCoverageScores = new Map<number, number>()
            const pageLineAnchorScores = new Map<number, number>()
            const pageKeywordAnchorScores = new Map<number, number>()
            const pageNumericAnchorScores = new Map<number, number>()
            const pageLineMatchedBounds = new Map<number, NormalizedHighlight[]>()
            const pageLineBlockRects = new Map<number, NormalizedHighlight>()

            if (clampedFocusPage && (lineAnchors.length > 0 || keywordAnchors.length > 0 || numericAnchors.length > 0)) {
                for (const pageNumber of targetPages) {
                    if (isCancelled) {
                        return
                    }

                    try {
                        if (lineVariantGroups.length > 0) {
                            let lineCoverage = 0
                            const matchedLineBounds: NormalizedHighlight[] = []
                            for (const lineVariants of lineVariantGroups) {
                                if (isCancelled) {
                                    return
                                }
                                const lineRects = await collectHighlightsForPage(pageNumber, lineVariants)
                                if (lineRects.length > 0) {
                                    lineCoverage += 1
                                    const lineBounds = getBoundingRectangle(lineRects)
                                    if (lineBounds) {
                                        matchedLineBounds.push(lineBounds)
                                    }
                                }
                            }
                            pageLineCoverageScores.set(pageNumber, lineCoverage)
                            if (matchedLineBounds.length > 0) {
                                pageLineMatchedBounds.set(pageNumber, matchedLineBounds)
                                const combinedLineBounds = getBoundingRectangle(matchedLineBounds)
                                if (combinedLineBounds) {
                                    pageLineBlockRects.set(pageNumber, combinedLineBounds)
                                }
                            }
                        }

                        const normalizedPageText = await collectNormalizedPageText(pageNumber)
                        pageLineAnchorScores.set(pageNumber, countContainedAnchors(normalizedPageText, lineAnchors))
                        pageKeywordAnchorScores.set(pageNumber, countContainedAnchors(normalizedPageText, keywordAnchors))
                        pageNumericAnchorScores.set(pageNumber, countContainedAnchors(normalizedPageText, numericAnchors))
                    } catch {
                        pageLineCoverageScores.set(pageNumber, 0)
                        pageLineAnchorScores.set(pageNumber, 0)
                        pageKeywordAnchorScores.set(pageNumber, 0)
                        pageNumericAnchorScores.set(pageNumber, 0)
                    }
                }
            }

            const getPageLineCoverageScore = (pageNumber: number): number => pageLineCoverageScores.get(pageNumber) ?? 0
            const getPageLineAnchorScore = (pageNumber: number): number => pageLineAnchorScores.get(pageNumber) ?? 0
            const getPageKeywordAnchorScore = (pageNumber: number): number => pageKeywordAnchorScores.get(pageNumber) ?? 0
            const getPageNumericAnchorScore = (pageNumber: number): number => pageNumericAnchorScores.get(pageNumber) ?? 0

            const highlightedTargetPages = targetPages.filter((pageNumber) => (nextHighlights[pageNumber]?.length ?? 0) > 0)
            let resolvedPage: number | null = null
            if (highlightedTargetPages.length > 0) {
                if (clampedFocusPage) {
                    const sortedByScore = [...highlightedTargetPages].sort((a, b) => {
                        const lineCoverageDiff = getPageLineCoverageScore(b) - getPageLineCoverageScore(a)
                        if (lineCoverageDiff !== 0) {
                            return lineCoverageDiff
                        }

                        const lineAnchorDiff = getPageLineAnchorScore(b) - getPageLineAnchorScore(a)
                        if (lineAnchorDiff !== 0) {
                            return lineAnchorDiff
                        }

                        const numericAnchorDiff = getPageNumericAnchorScore(b) - getPageNumericAnchorScore(a)
                        if (numericAnchorDiff !== 0) {
                            return numericAnchorDiff
                        }

                        const keywordAnchorDiff = getPageKeywordAnchorScore(b) - getPageKeywordAnchorScore(a)
                        if (keywordAnchorDiff !== 0) {
                            return keywordAnchorDiff
                        }

                        const highlightDiff = getPageHighlightCount(b) - getPageHighlightCount(a)
                        if (highlightDiff !== 0) {
                            return highlightDiff
                        }

                        if (a === clampedFocusPage) {
                            return -1
                        }
                        if (b === clampedFocusPage) {
                            return 1
                        }

                        const distanceDiff = Math.abs(a - clampedFocusPage) - Math.abs(b - clampedFocusPage)
                        if (distanceDiff !== 0) {
                            return distanceDiff
                        }

                        const forwardPreference = Number(b > clampedFocusPage) - Number(a > clampedFocusPage)
                        if (forwardPreference !== 0) {
                            return forwardPreference
                        }

                        return a - b
                    })
                    resolvedPage = sortedByScore[0] ?? null
                } else {
                    const sortedByHighlightCount = [...highlightedTargetPages].sort((a, b) => {
                        const highlightDiff = getPageHighlightCount(b) - getPageHighlightCount(a)
                        if (highlightDiff !== 0) {
                            return highlightDiff
                        }
                        return a - b
                    })
                    resolvedPage = sortedByHighlightCount[0] ?? null
                }
            }

            let finalHighlights = nextHighlights
            if (resolvedPage !== null && clampedFocusPage) {
                const buildFocusPageRects = (pageNumber: number): NormalizedHighlight[] => {
                    const lineCoverageBlock = pageLineBlockRects.get(pageNumber)
                    const lineCoverageScore = getPageLineCoverageScore(pageNumber)
                    if (preferBlockFocusHighlight && lineCoverageBlock && lineCoverageScore >= 2) {
                        return [lineCoverageBlock]
                    }

                    const matchedLineBounds = pageLineMatchedBounds.get(pageNumber) ?? []
                    if (preferBlockFocusHighlight && matchedLineBounds.length >= 2) {
                        const fallbackLineBlock = getBoundingRectangle(matchedLineBounds)
                        if (fallbackLineBlock) {
                            return [fallbackLineBlock]
                        }
                    }

                    return nextHighlights[pageNumber] ?? []
                }

                finalHighlights = {
                    [resolvedPage]: buildFocusPageRects(resolvedPage),
                }

                const resolvedCoverage = getPageLineCoverageScore(resolvedPage)
                const hasSplitCoverage = lineVariantGroups.length > 1 && resolvedCoverage > 0 && resolvedCoverage < lineVariantGroups.length

                if (preferBlockFocusHighlight && hasSplitCoverage) {
                    const adjacentPages = [resolvedPage - 1, resolvedPage + 1].filter(
                        (pageNumber) => pageNumber >= 1 && pageNumber <= totalPages
                    )
                    adjacentPages.forEach((pageNumber) => {
                        if (getPageLineCoverageScore(pageNumber) <= 0) {
                            return
                        }
                        const adjacentRects = buildFocusPageRects(pageNumber)
                        if (adjacentRects.length > 0) {
                            finalHighlights[pageNumber] = adjacentRects
                        }
                    })
                }
            }

            if (!isCancelled && resolvedPage === null && targetPages.length > 0) {
                const fallbackHighlights: Record<number, NormalizedHighlight[]> = {}
                const fallbackPages = Array.from({ length: totalPages }, (_, idx) => idx + 1).filter(
                    (pageNumber) => !visitedPages.has(pageNumber)
                )
                if (clampedFocusPage) {
                    fallbackPages.sort((a, b) => {
                        const distanceDiff = Math.abs(a - clampedFocusPage) - Math.abs(b - clampedFocusPage)
                        if (distanceDiff !== 0) {
                            return distanceDiff
                        }
                        return a - b
                    })
                }

                for (const pageNumber of fallbackPages) {
                    if (isCancelled) {
                        return
                    }
                    try {
                        const pageRects = await collectHighlightsForPage(pageNumber, variants)
                        if (pageRects.length > 0) {
                            fallbackHighlights[pageNumber] = pageRects
                            resolvedPage = pageNumber
                            break
                        }
                    } catch {
                        // Allow loop to keep searching other pages when highlight extraction fails.
                    }
                }

                if (Object.keys(fallbackHighlights).length > 0) {
                    finalHighlights = fallbackHighlights
                }
            }

            if (!isCancelled) {
                setFocusHighlights(finalHighlights)
                if (resolvedPage !== null && resolvedPage !== focusPage) {
                    onFocusHighlightChange({ term: focusTermRaw || trimmedFocusTerm, page: resolvedPage, align: focusAlign })
                }
            }
        }

        void computeFocus()

        return () => {
            isCancelled = true
        }
    }, [
        collectHighlightsForPage,
        collectNormalizedPageText,
        focusPage,
        focusAlign,
        focusTermRaw,
        normalizedFocusTerm,
        onFocusHighlightChange,
        pdfDocument,
        resetFocusHighlights,
        trimmedFocusTerm,
    ])

    useEffect(() => {
        if (!trimmedFocusTerm) {
            resetFocusHighlights()
        }
    }, [resetFocusHighlights, trimmedFocusTerm])

    useEffect(() => {
        if (!pdfDocument) {
            resetFocusHighlights()
        }
    }, [pdfDocument, resetFocusHighlights])

    return {
        focusHighlights,
        focusPage,
        resetFocusHighlights,
        trimmedFocusTerm,
    }
}
